import type { CustomVariablesDataPayload, SegmentsOptions } from '@openx/types/targeting/customVariables.ts';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTargetingContext } from '../../../utils/context/TargetingContext.tsx';
import {
  type ContextualSegmentIdentity,
  getContextualSegmentKey,
  parseCustomFormatToProviderSelections,
  resolveContextualProviderId,
} from '../utils/enrichment.ts';

const EMPTY_SEGMENTS: SegmentsOptions = [];

const getSelectedIdentities = (params: CustomVariablesDataPayload[]): ContextualSegmentIdentity[] =>
  parseCustomFormatToProviderSelections(params).flatMap(selection => [
    ...selection.includes.map(segmentId => ({ providerId: selection.providerId, segmentId })),
    ...selection.excludes.map(segmentId => ({ providerId: selection.providerId, segmentId })),
  ]);

const getSegmentMetadataKey = (segment: SegmentsOptions[number], providerIds: string[]): string | undefined => {
  const providerId = resolveContextualProviderId(segment, providerIds);
  return providerId ? getContextualSegmentKey({ providerId, segmentId: segment.id }) : undefined;
};

const mergeSegmentMetadata = (
  cachedSegments: SegmentsOptions,
  fetchedSegments: SegmentsOptions,
  providerIds: string[]
): SegmentsOptions => {
  if (fetchedSegments.length === 0) return cachedSegments;

  const knownKeys = new Set(cachedSegments.map(segment => getSegmentMetadataKey(segment, providerIds)).filter(Boolean));
  const newSegments = fetchedSegments.filter(segment => {
    const key = getSegmentMetadataKey(segment, providerIds);
    return key && !knownKeys.has(key);
  });

  return newSegments.length > 0 ? [...cachedSegments, ...newSegments] : cachedSegments;
};

export const useContextualSegmentMetadata = (
  accountId: string | undefined,
  providerIds: string[],
  params: CustomVariablesDataPayload[]
) => {
  const { useFetchSegments } = useTargetingContext();
  const selectedIdentities = useMemo(() => getSelectedIdentities(params), [params]);
  const metadataCacheRef = useRef<{ accountId?: string; segments: SegmentsOptions }>({ segments: [] });
  const cachedSegments =
    metadataCacheRef.current.accountId === accountId ? metadataCacheRef.current.segments : EMPTY_SEGMENTS;

  const cacheSegmentMetadata = useCallback(
    (segmentsToCache: SegmentsOptions) => {
      const accountSegments =
        metadataCacheRef.current.accountId === accountId ? metadataCacheRef.current.segments : EMPTY_SEGMENTS;
      metadataCacheRef.current = {
        accountId,
        segments: mergeSegmentMetadata(accountSegments, segmentsToCache, providerIds),
      };
    },
    [accountId, providerIds]
  );

  const selectedIdentityKeys = useMemo(
    () => new Set(selectedIdentities.map(getContextualSegmentKey)),
    [selectedIdentities]
  );
  const cachedIdentityKeys = useMemo(
    () => new Set(cachedSegments.map(segment => getSegmentMetadataKey(segment, providerIds)).filter(Boolean)),
    [cachedSegments, providerIds]
  );
  const missingSegmentIds = useMemo(() => {
    const ids = new Set<string>();
    selectedIdentities.forEach(identity => {
      if (!cachedIdentityKeys.has(getContextualSegmentKey(identity))) ids.add(identity.segmentId);
    });
    return Array.from(ids);
  }, [cachedIdentityKeys, selectedIdentities]);

  const { segments: fetchedSegments, loading } = useFetchSegments({
    account_id: accountId,
    segment_id: missingSegmentIds,
    limit: Math.max(missingSegmentIds.length * providerIds.length, 1),
    offset: 0,
  });
  const knownSegments = useMemo(
    () => mergeSegmentMetadata(cachedSegments, fetchedSegments, providerIds),
    [cachedSegments, fetchedSegments, providerIds]
  );
  const selectedSegments = useMemo(
    () =>
      selectedIdentityKeys.size === 0
        ? EMPTY_SEGMENTS
        : knownSegments.filter(segment => {
            const key = getSegmentMetadataKey(segment, providerIds);
            return key ? selectedIdentityKeys.has(key) : false;
          }),
    [knownSegments, providerIds, selectedIdentityKeys]
  );

  useEffect(() => {
    if (metadataCacheRef.current.accountId !== accountId || knownSegments !== cachedSegments) {
      metadataCacheRef.current = { accountId, segments: knownSegments };
    }
  }, [accountId, cachedSegments, knownSegments]);

  return {
    loading: missingSegmentIds.length > 0 && loading,
    segments: selectedSegments,
    cacheSegmentMetadata,
  };
};
